import React, { useContext, useMemo } from 'react'
import { Company, portalOptions, UserRole, companyOptions } from 'constant'
import { logout } from 'utils/auth'
import { isAdmin, getCurrentUserRole } from 'utils/userUtils'
import DashboardIcon from '@mui/icons-material/Dashboard'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import NavCard from 'components/NavCard'
import {
  AppBar,
  Box,
  Button,
  Container,
  Grid,
  Stack,
  Toolbar,
  Typography
} from '@mui/material'
import { AuthContext } from 'contexts/AuthContextProvider'

/**
 * Create company path map from companyOptions
 * This makes it easier to add new companies without hardcoding
 */
const companyPathMap: Record<string, string> = Object.entries(
  companyOptions
).reduce(
  (acc, [key, value]) => {
    acc[key] = value.path
    return acc
  },
  {} as Record<string, string>
)

const Home: React.FC = () => {
  const { currentUser } = useContext(AuthContext)
  const admin = isAdmin()
  const company = currentUser?.company
  const role = currentUser?.role ?? getCurrentUserRole()
  const companyEntries = useMemo(() => {
    if (admin) return []
    if (company && companyPathMap[company]) {
      return [
        { key: 'company-only', to: companyPathMap[company], title: company }
      ]
    }
    return []
  }, [admin, company])

  const managementEntries = useMemo(() => {
    const portals = admin
      ? portalOptions.map(p => ({
          key: `portal-${p.label}`,
          to: p.path,
          title: p.label
        }))
      : []

    const canSeeUserList = role === 'admin'
    const canSeeUserActions =
      role === UserRole.Admin.value ||
      role === UserRole.WarehouseM.value ||
      role === UserRole.WarehouseChina.value

    const cards = [...portals]
    if (canSeeUserList) {
      cards.push({
        key: 'users',
        to: 'users',
        title: 'User List'
      })
    }
    if (canSeeUserActions) {
      cards.push({
        key: 'user-actions',
        to: 'user-actions',
        title: 'User Actions'
      })
    }
    return cards
  }, [admin, role])

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position='sticky' color='default' enableColorOnDark>
        <Toolbar>
          <DashboardIcon sx={{ mr: 1 }} />
          <Typography variant='h6' sx={{ flexGrow: 1 }}>
            Portal
          </Typography>
          {admin && (
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              sx={{ mr: 2 }}
            >
              <AdminPanelSettingsIcon fontSize='small' />
              <Typography variant='body2'>Admin</Typography>
            </Stack>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth='lg' sx={{ py: 6 }}>
        {companyEntries.length > 0 && (
          <Box sx={{ mb: 5 }}>
            <Typography variant='overline' color='text.secondary'>
              Company
            </Typography>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {companyEntries.map(item => (
                <Grid key={item.key} size={{ xs: 12, sm: 6, md: 4 }}>
                  <NavCard to={item.to} title={item.title} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {managementEntries.length > 0 && (
          <Box>
            <Typography variant='overline' color='text.secondary'>
              Administration
            </Typography>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {managementEntries.map(item => (
                <Grid key={item.key} size={{ xs: 12, sm: 6, md: 4 }}>
                  <NavCard
                    to={item.to}
                    title={item.title}
                    subtitle={admin ? 'Admin only' : undefined}
                    actionLabel='Manage'
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
      <Button
        variant='contained'
        onClick={logout}
        disableRipple
        sx={{
          display: 'block',
          minHeight: 56,
          minWidth: 200,
          textTransform: 'none',
          fontSize: 16,
          px: 3,
          margin: '0 auto'
        }}
      >
        Logout
      </Button>
    </Box>
  )
}

export default Home
